/* Mount beside Pixify's main image without replacing its form, gallery or variant handlers. */
if (!customElements.get('product-tiling')) {
  customElements.define('product-tiling', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const {signal} = this.controller;
      this.block = document.getElementById(this.dataset.blockId);
      // Shopify can suffix block.id after rendering an app block. The rendered
      // Pixify wrapper immediately preceding this viewer is the source of truth.
      if (!this.block) {
        // Stylesheet tags can sit between the Pixify block and this viewer.
        let sibling = this.previousElementSibling;
        while (sibling && !sibling.matches('.shopify-app-block')) sibling = sibling.previousElementSibling;
        if (sibling?.querySelector('.pi-product-details__media-item')) this.block = sibling;
      }
      this.trigger = this.querySelector('.product-tiling__trigger');
      this.actions = this.querySelector('.product-viewer__actions') || this.trigger;
      this.appsTrigger = this.querySelector('[data-view-applications]');
      this.applications = this.querySelector('product-applications');
      this.dialog = this.querySelector('dialog');
      this.repeat = this.querySelector('swatch-repeat');
      if (!this.block || !this.trigger || !this.dialog || !this.repeat) return;
      this.trigger.addEventListener('click', () => this.open(), {signal});
      this.appsTrigger?.addEventListener('click', () => this.open('applications'), {signal});
      this.addEventListener('applications:availability', event => {
        if (this.appsTrigger) this.appsTrigger.hidden = !event.detail.available;
        this.positionTrigger();
        if (!event.detail.available && this.mode === 'applications' && this.dialog.open) this.dialog.close();
      }, {signal});
      this.applications?.refresh?.();
      this.dialog.querySelector('.product-tiling__close').addEventListener('click', () => this.dialog.close(), {signal});
      this.dialog.addEventListener('close', () => this.restorePage(), {signal});
      this.block.addEventListener('click', event => {
        // Only the original image: never intercept gallery arrows, license controls or purchase.
        if (event.target === this.source) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, {capture: true, signal});
      this.mount();
      this.imageResizeObserver = new ResizeObserver(() => this.positionTrigger());
      if (this.source) this.imageResizeObserver.observe(this.source);
      window.addEventListener('resize', () => this.positionTrigger(), {signal});
      this.observer = new MutationObserver(() => this.mount());
      this.observer.observe(this.block, {childList: true, subtree: true});
    }
    mount() {
      const source = this.block.querySelector('.pi-product-details__media-item img');
      if (!source) { this.actions.hidden = true; this.trigger.hidden = true; return; }
      if (source === this.source) return;
      this.imageController?.abort();
      this.imageController = new AbortController();
      this.source = source;
      this.imageResizeObserver?.disconnect();
      this.imageResizeObserver?.observe(source);
      const item = source.closest('.pi-product-details__media-item');
      this.actions.hidden = true;
      item.append(this.actions);
      source.closest('.pi-product-details__media')?.removeAttribute('data-zoom-enabled');
      this.repeat.setSource(source);
      const ready = () => { this.actions.hidden = !source.naturalWidth; this.trigger.hidden = !source.naturalWidth; this.positionTrigger(); this.applications?.refresh?.(); };
      source.addEventListener('load', ready, {signal: this.imageController.signal});
      source.addEventListener('error', () => {
        this.trigger.hidden = true;
        this.actions.hidden = true;
        if (this.dialog.open) this.dialog.close();
      }, {signal: this.imageController.signal});
      if (source.complete) ready();
    }
    positionTrigger() {
      if (!this.source || this.trigger.hidden) return;
      const image = this.source.getBoundingClientRect();
      const item = this.source.closest('.pi-product-details__media-item').getBoundingClientRect();
      // Anchor to the visible image, not Pixify's wider media column.
      const fit = Math.min(image.width / this.source.naturalWidth, image.height / this.source.naturalHeight);
      const width = this.source.naturalWidth * fit;
      const height = this.source.naturalHeight * fit;
      this.actions.style.right = Math.max(12, item.right - image.right + (image.width - width) / 2 + 12) + 'px';
      this.actions.style.bottom = Math.max(12, item.bottom - image.bottom + (image.height - height) / 2 + 12) + 'px';
      this.actions.style.maxWidth = Math.max(0, width - 24) + 'px';
    }
    open(mode = 'tiling', trigger) {
      if (this.dialog.open || !this.source?.naturalWidth) return;
      if (mode === 'applications' && !this.applications?.open?.()) return;
      this.mode = mode;
      this.activeTrigger = trigger || (mode === 'applications' ? this.appsTrigger : this.trigger);
      this.repeat.hidden = mode === 'applications';
      if (this.applications) this.applications.hidden = mode !== 'applications';
      const title = mode === 'applications' ? 'Applications' : 'Seamless tiling';
      this.dialog.querySelector('h2').textContent = title;
      this.dialog.setAttribute('aria-label', title + ' — ' + (this.dataset.productTitle || this.dialog.querySelector('header p')?.textContent || ''));
      this.dialog.querySelector('.product-tiling__close').setAttribute('aria-label', 'Close ' + title.toLowerCase());
      if (mode === 'tiling') { this.repeat.setSource(this.source); this.repeat.reset(); }
      this.scrollState = {x: window.scrollX, y: window.scrollY,
        styles: ['position','top','left','right','width','overflow','padding-right'].map(key => [key, document.body.style.getPropertyValue(key), document.body.style.getPropertyPriority(key)])};
      const gap = window.innerWidth - document.documentElement.clientWidth;
      const padding = parseFloat(getComputedStyle(document.body).paddingRight) || 0;
      document.body.style.position = 'fixed';
      document.body.style.top = -this.scrollState.y + 'px';
      document.body.style.left = -this.scrollState.x + 'px';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      if (gap > 0) document.body.style.paddingRight = (padding + gap) + 'px';
      this.dialog.showModal();
      if (mode === 'tiling') this.repeat.update();
      else this.applications.fit();
      this.activeTrigger.setAttribute('aria-expanded', 'true');
    }
    restorePage() {
      this.applications?.close?.();
      if (!this.scrollState) return;
      const state = this.scrollState;
      this.scrollState = null;
      for (const [key, value, priority] of state.styles) {
        if (value) document.body.style.setProperty(key, value, priority);
        else document.body.style.removeProperty(key);
      }
      const root = document.documentElement;
      const behavior = root.style.getPropertyValue('scroll-behavior');
      const priority = root.style.getPropertyPriority('scroll-behavior');
      root.style.setProperty('scroll-behavior', 'auto', 'important');
      window.scrollTo(state.x, state.y);
      if (behavior) root.style.setProperty('scroll-behavior', behavior, priority);
      else root.style.removeProperty('scroll-behavior');
      const trigger = this.activeTrigger || this.trigger;
      trigger.setAttribute('aria-expanded', 'false');
      if (trigger.isConnected) trigger.focus({preventScroll: true});
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.imageResizeObserver?.disconnect();
      this.imageController?.abort();
      this.controller?.abort();
      this.controller = null;
      if (this.dialog?.open) this.dialog.close();
      this.restorePage();
      // The trigger lives inside Pixify, outside this custom element.
      if (this.actions) { this.actions.hidden = true; this.append(this.actions); }
      this.source = null;
    }
  });
}
